import React from 'react';
import { motion } from 'framer-motion';
import {
  Shield, Lock, CheckCircle, Eye, Server, Database,
  Zap, RefreshCw, Award, Globe, FileCheck, Layers, Mail, AlertTriangle
} from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Security.css';
import { SecurityHero } from '../components/PageHeroes';

const Security = () => {
  // Color removed from data — features, infrastructure, and
  // certifications all now share one card pattern instead of three
  // slightly different shapes each colored at random.
  const securityFeatures = [
    {
      icon: Shield,
      title: 'AES-256 Encryption',
      description: 'All data encrypted with AES-256 both in transit (TLS 1.3) and at rest across every server and backup.'
    },
    {
      icon: Lock,
      title: 'Secure Authentication',
      description: 'Two-factor authentication (2FA) and OAuth 2.0 with session timeout and brute-force protection built in.'
    },
    {
      icon: CheckCircle,
      title: 'Annual Security Audits',
      description: 'Independent third-party penetration testing and security audits carried out every year.'
    },
    {
      icon: Eye,
      title: 'Compliance Monitoring',
      description: 'Continuous automated monitoring for UK PAYE, HMRC RTI, VAT filing, and GDPR regulatory compliance.'
    },
    {
      icon: Layers,
      title: 'Role-Based Access',
      description: 'Granular permission controls ensure each user accesses only what they need — nothing more.'
    },
    {
      icon: FileCheck,
      title: 'Full Audit Trails',
      description: 'Every action is logged with timestamps and user IDs, giving you a complete immutable audit history.'
    }
  ];

  const infraItems = [
    {
      icon: Server,
      title: '99.9% Uptime SLA',
      description: 'Redundant servers across multiple UK data centres with automatic failover and load balancing.'
    },
    {
      icon: Database,
      title: 'Daily Automated Backups',
      description: 'Multiple encrypted backup copies stored in geographically distributed UK locations every 24 hours.'
    },
    {
      icon: Zap,
      title: 'DDoS Protection',
      description: 'Enterprise-grade DDoS mitigation and rate limiting to keep services available under attack.'
    },
    {
      icon: RefreshCw,
      title: 'Disaster Recovery',
      description: 'Comprehensive DR plan with a 15-minute Recovery Time Objective (RTO) and tested quarterly.'
    }
  ];

  const certifications = [
    {
      icon: Award,
      name: 'ISO 27001',
      description: 'Information Security Management — internationally recognised standard for securing data assets.'
    },
    {
      icon: Globe,
      name: 'GDPR Compliant',
      description: 'Full compliance with the UK General Data Protection Regulation and Data Protection Act 2018.'
    },
    {
      icon: FileCheck,
      name: 'UK PAYE Certified',
      description: 'HMRC-approved payroll platform authorised for Real Time Information (RTI) submissions.'
    },
    {
      icon: Shield,
      name: 'SOC 2 Type II',
      description: 'Service Organisation Control audit verifying security, availability, and confidentiality controls.'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } }
  };

  return (
    <div className="security-page">
      <SecurityHero />

      {/* ===== SECURITY FEATURES ===== */}
      <section className="sec-section">
        <div className="container">
          <motion.div
            className="sec-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="sec-eyebrow">Core security</span>
            <h2>Security features</h2>
            <p>Every layer of our platform is designed with your data protection as the top priority.</p>
          </motion.div>

          <motion.div
            className="sec-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {securityFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={idx}
                  className="sec-card"
                  variants={itemVariants}
                >
                  <div className="sec-icon-box">
                    <Icon size={22} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== INFRASTRUCTURE ===== */}
      <section className="sec-section sec-section-alt">
        <div className="container">
          <motion.div
            className="sec-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="sec-eyebrow">Infrastructure</span>
            <h2>Reliability &amp; uptime</h2>
            <p>Enterprise infrastructure built for the demands of UK payroll, financial, and regulatory compliance.</p>
          </motion.div>

          <motion.div
            className="sec-grid sec-grid-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {infraItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={idx}
                  className="sec-card"
                  variants={itemVariants}
                >
                  <div className="sec-icon-box">
                    <Icon size={22} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== CERTIFICATIONS ====================
          Given slightly more visual weight than the two grids above —
          these are externally verified claims, not just product
          features, so they get a stronger card treatment and a
          consistent "verified" badge instead of per-item color. */}
      <section className="sec-section">
        <div className="container">
          <motion.div
            className="sec-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="sec-eyebrow">Trust &amp; compliance</span>
            <h2>Certifications &amp; compliance</h2>
            <p>Independently verified and certified to the highest UK and international standards.</p>
          </motion.div>

          <motion.div
            className="sec-cert-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {certifications.map((cert, idx) => {
              const Icon = cert.icon;
              return (
                <motion.div
                  key={idx}
                  className="sec-cert-card"
                  variants={itemVariants}
                >
                  <div className="sec-cert-icon-wrap">
                    <Icon size={28} />
                  </div>
                  <h3>{cert.name}</h3>
                  <p>{cert.description}</p>
                  <div className="sec-cert-footer">
                    <CheckCircle size={14} />
                    <span>Verified &amp; active</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== INCIDENT RESPONSE ===== */}
      <section className="sec-section sec-section-alt">
        <div className="container">
          <motion.div
            className="sec-section-header"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="sec-eyebrow">Responsible disclosure</span>
            <h2>Incident response policy</h2>
            <p>We take every security report seriously and act fast.</p>
          </motion.div>

          <motion.div
            className="sec-incident-card"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="sec-incident-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="sec-incident-body">
              <h3>Found a vulnerability?</h3>
              <p>
                We maintain a responsible disclosure policy. If you discover a security vulnerability, email us at{' '}
                <a href="mailto:thomas@effectivestaff.co.uk">thomas@effectivestaff.co.uk</a> with full details.
                We will acknowledge your report within 24 hours and work collaboratively to resolve the issue promptly.
              </p>
              <div className="sec-incident-steps">
                {['Report via email with details', 'Acknowledgement within 24 hours', 'Fix deployed and verified', 'Credited in our security hall of fame'].map((step, i) => (
                  <div key={i} className="sec-incident-step">
                    <div className="sec-step-num">{i + 1}</div>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="sec-cta-section">
        <div className="container">
          <motion.div
            className="sec-cta-card"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="sec-cta-icon">
              <Mail size={24} />
            </div>
            <h2>Questions about security?</h2>
            <p>Our security team is ready to answer any questions about our infrastructure and practices.</p>
            <a href="mailto:thomas@effectivestaff.co.uk" className="sec-cta-btn">
              Contact security team
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Security;